import { Box } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  type LandingPageBlock,
  resolveLandingPageBlockMinHeight,
  resolveLandingPageLogoUrl,
} from '@provision/landing-page-utils';
import { type ReactNode, useMemo } from 'react';
import { buildBlockLayout, resolveBlockBackground } from './block-background';
import { ClosingCtaBlock, ContentBlock, FaqBlock, HeroBlock, PlansBlock } from './blocks';
import { SiteFooter } from './chrome/site-footer';
import { TopBar } from './chrome/top-bar';
import { resolveLandingColors } from './colors';
import { DEFAULT_LAYOUT_ID, LAYOUT_DEFINITIONS } from './layouts';
import { BlockSurfaceContext } from './layouts/block-surface-context';
import { buildLayout } from './layouts/build-layout';
import { useLayout, useLayoutStyles } from './layouts/layout-context';
import { LayoutProvider } from './layouts/layout-provider';
import type { BlockRenderProps, LandingLayout, Sx } from './layouts/types';
import { deriveSavingsPercent } from './savings';
import { landingPageTheme } from './theme';
import type { LandingPagePlansData, LandingPageProps } from './types';
import { useWebfonts } from './use-webfonts';

const NO_PLANS: LandingPagePlansData = {};

function renderBlock(props: BlockRenderProps, layout: LandingLayout) {
  const Override = layout.components?.[props.block.type];
  if (Override) return <Override {...props} />;

  switch (props.block.type) {
    case 'hero':
      return <HeroBlock {...props} />;
    case 'content':
      return <ContentBlock {...props} />;
    case 'plans':
      return <PlansBlock {...props} />;
    case 'faq':
      return <FaqBlock {...props} />;
    case 'closingCta':
      return <ClosingCtaBlock {...props} />;
    default:
      // A newer portal's block type. A public page must never white-screen on one.
      return null;
  }
}

// Every block renders one root, which stretches to the set height so its own band fills it, and
// centers what it holds. Content taller than the height grows the section rather than clipping.
function minHeightSx(minHeight: number): Sx {
  if (!minHeight) return {};

  return { display: 'grid', minHeight, '& > *': { display: 'grid', alignContent: 'center' } };
}

/** A block's section: its in-page anchor, its own background and its height when it has them. */
const BlockSection = ({
  block,
  children,
}: {
  block: LandingPageBlock;
  children: ReactNode;
}) => {
  const page = useLayout();
  const { fill, color, imageUrl, imageFit, imageFocusX, imageFocusY } = block.background ?? {};
  // Keyed on the values, never `block.background`: react-hook-form hands back a fresh object.
  const own = useMemo(() => {
    if (!fill) return undefined;
    const background = resolveBlockBackground(
      { fill, color, imageUrl, imageFit, imageFocusX, imageFocusY },
      block.type,
      page.tokens,
    );
    return (
      background && {
        ...background,
        layout: buildBlockLayout(page, block.type, background),
        context: { surface: background.surface, page },
      }
    );
  }, [page, block.type, fill, color, imageUrl, imageFit, imageFocusX, imageFocusY]);
  const heightSx = minHeightSx(resolveLandingPageBlockMinHeight(block));

  if (!own) {
    return (
      <Box component="section" id={block.id} data-block-type={block.type} sx={heightSx}>
        {children}
      </Box>
    );
  }

  return (
    <Box
      component="section"
      id={block.id}
      data-block-type={block.type}
      data-surface={own.surface}
      sx={{
        ...heightSx,
        background: own.css,
        color: own.surface === 'dark' ? page.tokens.onDark : page.tokens.ink,
      }}
    >
      <BlockSurfaceContext.Provider value={own.context}>
        <LayoutProvider layout={own.layout}>{children}</LayoutProvider>
      </BlockSurfaceContext.Provider>
    </Box>
  );
};

type LandingPageBodyProps = Required<Pick<LandingPageProps, 'config' | 'plans'>> &
  Pick<LandingPageProps, 'partner' | 'logoUrl' | 'onCta' | 'pendingCta'>;

/** Exported for the layout-seam test, which renders it under a stub layout. */
export const LandingPageBody = ({
  config,
  plans,
  partner,
  logoUrl,
  onCta,
  pendingCta,
}: LandingPageBodyProps) => {
  const layout = useLayout();
  const shared = useLayoutStyles('shared');
  const plansBlockId = config.blocks.find((block) => block.type === 'plans')?.id;
  const faqBlockId = config.blocks.find((block) => block.type === 'faq')?.id;
  const savingsPercent = useMemo(
    () => deriveSavingsPercent(plans.products ?? []),
    [plans.products],
  );
  let contentIndex = 0;

  return (
    <Box sx={shared.page}>
      <TopBar
        settings={config.settings}
        logoUrl={resolveLandingPageLogoUrl(config.settings, logoUrl)}
        brandName={partner?.name}
        plansBlockId={plansBlockId}
        faqBlockId={faqBlockId}
        onCta={onCta}
        pendingCta={pendingCta}
      />
      <Box component="main">
        {config.blocks.map((block) => (
          <BlockSection key={block.id} block={block}>
            {renderBlock(
              {
                block,
                plans,
                settings: config.settings,
                plansBlockId,
                contentIndex: block.type === 'content' ? contentIndex++ : 0,
                savingsPercent,
                onCta,
                pendingCta,
              },
              layout,
            )}
          </BlockSection>
        ))}
      </Box>
      <SiteFooter partner={partner} />
    </Box>
  );
};

/**
 * The one renderer for both the portal preview and the public page, so they cannot drift.
 * Blocks render in config order, each in a section whose id is its in-page anchor.
 */
export const LandingPage = ({
  config,
  plans = NO_PLANS,
  partner,
  logoUrl,
  onCta,
  pendingCta,
  layout: layoutId = DEFAULT_LAYOUT_ID,
  disableFontLoading = false,
}: LandingPageProps) => {
  const { primary, secondary, accent } = config.settings.colors;
  // Keyed on the colour strings, never `settings`: react-hook-form emits a shallow spread, so the
  // branch keeps its identity across edits and a memo on the object would never invalidate.
  const layout = useMemo(
    () =>
      buildLayout(
        LAYOUT_DEFINITIONS[layoutId],
        resolveLandingColors({ primary, secondary, accent }),
      ),
    [layoutId, primary, secondary, accent],
  );

  useWebfonts(layout.fontsHref, !disableFontLoading);

  return (
    <ThemeProvider theme={landingPageTheme}>
      <LayoutProvider layout={layout}>
        <LandingPageBody
          config={config}
          plans={plans}
          partner={partner}
          logoUrl={logoUrl}
          onCta={onCta}
          pendingCta={pendingCta}
        />
      </LayoutProvider>
    </ThemeProvider>
  );
};
